import { reportUiFailure } from "$lib/report-failure.js";

import {
  desktopCredentials,
  readDesktopCredential,
  persistDesktopCredential,
  credentialUnavailable,
} from "./desktop-credentials.js";
// The saved login session and the app's rules for restoring it. Depends only on
// leaf helpers so client telemetry can read it before the app state module loads.
import { CLOUD_RELAY_WS_URL } from "./relay-config.js";

export const SESSION_KEY = "cyborg7-session";

export interface SavedSession {
  url: string;
  token: string;
}

// Old builds saved RAW IPs of retired cloud-relay boxes; those hosts (VITE_LEGACY_CLOUD_HOSTS,
// comma-separated, never hard-coded) migrate to the build's relay so stale sessions self-heal.
const LEGACY_CLOUD_HOSTS = new Set(
  ((import.meta.env.VITE_LEGACY_CLOUD_HOSTS as string | undefined) ?? "")
    .split(",")
    .map((host) => host.trim())
    .filter((host) => host.length > 0),
);

function migrateSessionUrl(url: string): string {
  try {
    if (LEGACY_CLOUD_HOSTS.has(new URL(url).hostname)) return CLOUD_RELAY_WS_URL;
  } catch {
    // intentional: a malformed saved URL just isn't migrated; returned as-is.
  }
  return url;
}

// Dev on localhost only: a saved session on a non-local relay is treated as absent, so boot
// lands on /login (local relay) instead of auto-connecting to the cloud. Prod/desktop unaffected.
const isDevLocalhost =
  import.meta.env.DEV &&
  typeof location !== "undefined" &&
  (location.hostname === "localhost" || location.hostname === "127.0.0.1");

function isLocalRelayUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return host === "localhost" || host === "127.0.0.1";
  } catch {
    // intentional: a malformed URL is not provably local; boot falls through to /login.
    return false;
  }
}

let savedSessionReadReported = false;

export function getSavedSession(): SavedSession | null {
  if (typeof window === "undefined") return null; // null: SSR has no browser credential
  const desktop = readDesktopCredential();
  if (desktop?.status === "ok") {
    const url = migrateSessionUrl(desktop.value.url);
    if (isDevLocalhost && !isLocalRelayUrl(url)) return null; // null: dev restores local relays only
    if (url !== desktop.value.url)
      persistDesktopCredential(url, desktop.value.token, desktop.value.refreshToken);
    return { url, token: desktop.value.token };
  }
  return readBrowserSession();
}

function readBrowserSession(): SavedSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.url && parsed?.token) {
      const url = migrateSessionUrl(parsed.url);
      if (isDevLocalhost && !isLocalRelayUrl(url)) return null;
      // Persist the heal; the token stays valid (the cloud relays share one JWT secret).
      if (url !== parsed.url) {
        try {
          localStorage.setItem(SESSION_KEY, JSON.stringify({ ...parsed, url }));
        } catch {
          // intentional: best-effort write-back; this run still uses the healed URL.
        }
      }
      if (desktopCredentials()) {
        const refresh = localStorage.getItem("cyborg7-refresh-token");
        persistDesktopCredential(url, parsed.token, refresh || undefined);
      }
      return { url, token: parsed.token };
    }
  } catch {
    if (desktopCredentials()) credentialUnavailable();
    if (!savedSessionReadReported) {
      // Telemetry also reads this session: claim before reporting to stop recursion.
      savedSessionReadReported = true;
      reportUiFailure("auth.saved_session", new Error("Saved sign-in could not be read"));
    }
  }
  return null;
}
